<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <div class="d-flex align-center mb-4">
          <v-btn
            @click="$router.push('/lists')"
            icon="mdi-arrow-left"
            variant="text"
            class="me-3"
          ></v-btn>
          <h1 class="text-h4">
            <v-icon class="me-2">mdi-shield-account</v-icon>
            Admin Panel
          </h1>
        </div>
      </v-col>

      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-icon class="me-2">mdi-image-auto-adjust</v-icon>
            Image Generation
            <v-spacer></v-spacer>
            <v-chip
              :color="imageSettings.available ? 'success' : 'warning'"
              variant="tonal"
              size="small"
            >
              {{ imageSettings.available ? 'Available' : 'Unavailable' }}
            </v-chip>
          </v-card-title>

          <v-card-text>
            <v-alert v-if="imageSettingsError" type="error" class="mb-4">
              {{ imageSettingsError }}
            </v-alert>
            <v-alert v-if="imageSettingsSuccess" type="success" class="mb-4">
              {{ imageSettingsSuccess }}
            </v-alert>
            <v-alert
              v-if="!imageSettingsLoading && !imageSettings.available"
              type="warning"
              variant="tonal"
              class="mb-4"
            >
              Image generation stays disabled for list and group dialogs until this
              configuration is enabled and has an API key.
            </v-alert>

            <div v-if="imageSettingsLoading" class="text-center py-8">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <p class="mt-4">Loading image generation settings...</p>
            </div>

            <v-form v-else v-model="imageSettingsValid" @submit.prevent="saveImageSettings">
              <v-row>
                <v-col cols="12" md="4">
                  <v-switch
                    v-model="imageSettings.enabled"
                    label="Enable image generation"
                    color="primary"
                    hide-details
                  ></v-switch>
                </v-col>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="imageSettings.provider"
                    :items="providerOptions"
                    label="Provider"
                    variant="outlined"
                    :rules="[v => !!v || 'Provider is required']"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="4" class="d-flex align-center">
                  <div>
                    <div class="text-caption text-medium-emphasis">Current source</div>
                    <v-chip size="small" variant="tonal">
                      {{ formatSource(imageSettings.source) }}
                    </v-chip>
                  </div>
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="imageSettings.apiKey"
                    label="Runware API key"
                    :type="showApiKey ? 'text' : 'password'"
                    :append-inner-icon="showApiKey ? 'mdi-eye-off' : 'mdi-eye'"
                    @click:append-inner="showApiKey = !showApiKey"
                    @update:model-value="onApiKeyInput"
                    variant="outlined"
                    autocomplete="new-password"
                    :placeholder="imageSettings.api_key_configured ? 'Leave blank to keep the current key' : 'Enter a Runware API key'"
                    :hint="apiKeyHint"
                    persistent-hint
                  ></v-text-field>
                  <v-checkbox
                    v-if="imageSettings.api_key_source === 'database'"
                    v-model="clearStoredApiKey"
                    label="Remove the saved key and fall back to RUNWARE_API_KEY"
                    density="compact"
                    hide-details
                  ></v-checkbox>
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="imageSettings.model"
                    label="Runware model"
                    variant="outlined"
                    :rules="[v => !!v?.trim() || 'Model is required']"
                  ></v-text-field>
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="imageSettings.style_prompt"
                    label="Default style prompt"
                    rows="4"
                    variant="outlined"
                    :rules="[v => !!v?.trim() || 'Style prompt is required']"
                    hint="Prepended to list header and group image prompts."
                    persistent-hint
                  ></v-textarea>
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="imageSettings.advancedParamsText"
                    label="Advanced Runware parameters (JSON)"
                    rows="12"
                    variant="outlined"
                    class="json-editor"
                    hint="Model and positivePrompt are managed separately and cannot be set here."
                    persistent-hint
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>

          <v-card-actions v-if="!imageSettingsLoading">
            <v-btn
              color="warning"
              variant="text"
              @click="resetImageSettings"
              :disabled="imageSettingsSaving"
            >
              Reset to environment
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              @click="saveImageSettings"
              :loading="imageSettingsSaving"
              :disabled="!imageSettingsValid || imageSettingsSaving"
              prepend-icon="mdi-content-save"
            >
              Save settings
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>

      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-icon class="me-2">mdi-account-group</v-icon>
            Registered Users
            <v-spacer></v-spacer>
            <v-btn @click="loadUsers" :loading="usersLoading" color="primary" variant="text">
              <v-icon>mdi-refresh</v-icon>
              Refresh
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-alert v-if="usersError" type="error" class="mb-4">
              {{ usersError }}
            </v-alert>

            <v-data-table
              v-if="!usersLoading && users.length > 0"
              :headers="headers"
              :items="users"
              :loading="usersLoading"
              class="elevation-1"
              item-value="id"
            >
              <template #item.role="{ item }">
                <v-chip
                  :color="item.role === 'ADMIN' ? 'warning' : 'primary'"
                  variant="tonal"
                  size="small"
                >
                  <v-icon start>
                    {{ item.role === 'ADMIN' ? 'mdi-shield-account' : 'mdi-account' }}
                  </v-icon>
                  {{ item.role }}
                </v-chip>
              </template>

              <template #item.created_at="{ item }">
                {{ formatDate(item.created_at) }}
              </template>

              <template #item.packlist_count="{ item }">
                <v-chip color="info" variant="tonal" size="small">
                  {{ item.packlist_count }}
                </v-chip>
              </template>
            </v-data-table>

            <div v-else-if="!usersLoading && users.length === 0" class="text-center py-8">
              <v-icon size="64" color="grey">mdi-account-off</v-icon>
              <p class="text-h6 mt-4">No users found</p>
            </div>

            <div v-if="usersLoading" class="text-center py-8">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <p class="mt-4">Loading users...</p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios'
import { useAuthStore } from '../stores/auth'
import { useImageGenerationConfig } from '../composables/useImageGenerationConfig'

export default {
  name: 'Admin',
  setup() {
    const authStore = useAuthStore()
    const { loadImageGenerationStatus } = useImageGenerationConfig()
    return { authStore, loadImageGenerationStatus }
  },

  data() {
    return {
      users: [],
      usersLoading: false,
      usersError: null,
      imageSettingsLoading: false,
      imageSettingsSaving: false,
      imageSettingsValid: true,
      imageSettingsError: null,
      imageSettingsSuccess: null,
      showApiKey: false,
      clearStoredApiKey: false,
      providerOptions: [
        { title: 'Runware', value: 'runware' }
      ],
      imageSettings: {
        enabled: false,
        provider: 'runware',
        available: false,
        apiKey: '',
        api_key_configured: false,
        api_key_source: 'none',
        model: '',
        style_prompt: '',
        advancedParamsText: '{}',
        source: 'defaults'
      },
      headers: [
        { title: 'Username', key: 'username', align: 'start' },
        { title: 'Role', key: 'role', align: 'center' },
        { title: 'Registration Date', key: 'created_at', align: 'center' },
        { title: 'Number of Lists', key: 'packlist_count', align: 'center' }
      ]
    }
  },

  computed: {
    apiKeyHint() {
      if (this.imageSettings.api_key_source === 'database') {
        return 'An encrypted key is saved in the Packlist database.'
      }
      if (this.imageSettings.api_key_source === 'environment') {
        return 'Using RUNWARE_API_KEY until a key is saved here.'
      }
      if (this.imageSettings.api_key_source === 'invalid') {
        return 'The saved key cannot be decrypted. Enter and save it again.'
      }
      return 'No API key is currently configured.'
    }
  },

  async mounted() {
    if (this.authStore.user?.role !== 'ADMIN') {
      this.$router.push('/lists')
      return
    }

    await Promise.all([this.loadUsers(), this.loadImageSettings()])
  },

  methods: {
    applyImageSettings(settings) {
      this.imageSettings = {
        ...settings,
        apiKey: '',
        advancedParamsText: JSON.stringify(settings.advanced_params || {}, null, 2)
      }
      this.clearStoredApiKey = false
      this.showApiKey = false
    },

    async loadImageSettings() {
      this.imageSettingsLoading = true
      this.imageSettingsError = null
      try {
        const response = await axios.get('/api/admin/image-generation-settings')
        this.applyImageSettings(response.data)
      } catch (error) {
        this.imageSettingsError = error.response?.data?.detail || 'Failed to load image generation settings'
        console.error('Error loading image generation settings:', error)
      } finally {
        this.imageSettingsLoading = false
      }
    },

    async saveImageSettings() {
      this.imageSettingsError = null
      this.imageSettingsSuccess = null

      let advancedParams
      try {
        advancedParams = JSON.parse(this.imageSettings.advancedParamsText)
        if (!advancedParams || Array.isArray(advancedParams) || typeof advancedParams !== 'object') {
          throw new Error('Advanced parameters must be a JSON object')
        }
      } catch (error) {
        this.imageSettingsError = error.message || 'Advanced parameters must be valid JSON'
        return
      }

      this.imageSettingsSaving = true
      try {
        const payload = {
          enabled: this.imageSettings.enabled,
          provider: this.imageSettings.provider,
          model: this.imageSettings.model,
          style_prompt: this.imageSettings.style_prompt,
          advanced_params: advancedParams,
          clear_api_key: this.clearStoredApiKey
        }
        if (this.imageSettings.apiKey.trim()) {
          payload.api_key = this.imageSettings.apiKey.trim()
        }

        const response = await axios.put('/api/admin/image-generation-settings', payload)
        this.applyImageSettings(response.data)
        await this.loadImageGenerationStatus(true)
        this.imageSettingsSuccess = 'Image generation settings saved.'
      } catch (error) {
        const detail = error.response?.data?.detail
        this.imageSettingsError = Array.isArray(detail)
          ? detail.map(item => item.msg).join(', ')
          : detail || 'Failed to save image generation settings'
        console.error('Error saving image generation settings:', error)
      } finally {
        this.imageSettingsSaving = false
      }
    },

    async resetImageSettings() {
      if (!confirm('Reset all saved image generation settings and use environment values?')) return

      this.imageSettingsSaving = true
      this.imageSettingsError = null
      this.imageSettingsSuccess = null
      try {
        const response = await axios.delete('/api/admin/image-generation-settings')
        this.applyImageSettings(response.data)
        await this.loadImageGenerationStatus(true)
        this.imageSettingsSuccess = 'Saved settings removed; environment values are active.'
      } catch (error) {
        this.imageSettingsError = error.response?.data?.detail || 'Failed to reset image generation settings'
        console.error('Error resetting image generation settings:', error)
      } finally {
        this.imageSettingsSaving = false
      }
    },

    onApiKeyInput(value) {
      if (value) this.clearStoredApiKey = false
    },

    formatSource(source) {
      return {
        database: 'Admin settings',
        environment: 'Environment',
        defaults: 'Built-in defaults'
      }[source] || source
    },

    async loadUsers() {
      this.usersLoading = true
      this.usersError = null
      try {
        const response = await axios.get('/api/admin/users')
        this.users = response.data
      } catch (error) {
        this.usersError = error.response?.data?.detail || 'Failed to load users'
        console.error('Error loading users:', error)
      } finally {
        this.usersLoading = false
      }
    },

    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.v-data-table {
  background-color: transparent;
}

.json-editor :deep(textarea) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.875rem;
}
</style>
